import type { ProjectScan, ServiceDependenciesResult, ServiceDependencyItem } from '../../core-open/index.js';

/** Maps package names to service display names, grouped by category */
const SERVICE_CATEGORIES: Record<keyof ServiceDependenciesResult, Record<string, string>> = {
  payment: {
    'stripe': 'Stripe',
    '@stripe/stripe-js': 'Stripe.js',
    '@stripe/react-stripe-js': 'Stripe React',
    'braintree': 'Braintree',
    'braintree-web': 'Braintree Web',
    '@paypal/checkout-server-sdk': 'PayPal',
    '@paypal/react-paypal-js': 'PayPal React',
    '@paypal/paypal-js': 'PayPal.js',
    'square': 'Square',
    'razorpay': 'Razorpay',
    'adyen-api-library': 'Adyen',
    '@adyen/api-library': 'Adyen',
    '@adyen/adyen-web': 'Adyen Web',
    'mollie-api-node': 'Mollie',
    '@lemonsqueezy/lemonsqueezy.js': 'Lemon Squeezy',
    'paddle-sdk': 'Paddle',
    '@paddle/paddle-node-sdk': 'Paddle',
    'coinbase-commerce-node': 'Coinbase Commerce',
    'gocardless-nodejs': 'GoCardless',
    'klarna-checkout': 'Klarna',
    '@recurly/recurly-js': 'Recurly',
    'chargebee': 'Chargebee',
  },
  auth: {
    '@auth0/auth0-spa-js': 'Auth0 SPA',
    '@auth0/auth0-react': 'Auth0 React',
    '@auth0/nextjs-auth0': 'Auth0 Next.js',
    'auth0': 'Auth0',
    '@clerk/clerk-js': 'Clerk.js',
    '@clerk/clerk-sdk-node': 'Clerk (Node)',
    '@clerk/nextjs': 'Clerk (Next.js)',
    '@clerk/react-router': 'Clerk (React Router)',
    '@clerk/tanstack-react-start': 'Clerk (TanStack Start)',
    '@clerk/expo': 'Clerk (Expo)',
    '@clerk/astro': 'Clerk (Astro)',
    '@clerk/chrome-extension': 'Clerk (Chrome Extension)',
    '@clerk/express': 'Clerk (Express)',
    '@clerk/fastify': 'Clerk (Fastify)',
    '@clerk/hono': 'Clerk (Hono)',
    '@clerk/nuxt': 'Clerk (Nuxt)',
    '@clerk/react': 'Clerk (React)',
    '@clerk/vue': 'Clerk (Vue)',
    '@clerk/clerk-react': 'Clerk (React)',
    'firebase-admin': 'Firebase Admin',
    'firebase': 'Firebase',
    '@firebase/auth': 'Firebase Auth',
    'passport': 'Passport.js',
    'passport-local': 'Passport Local',
    'passport-google-oauth20': 'Passport Google',
    'passport-github2': 'Passport GitHub',
    'next-auth': 'NextAuth',
    '@auth/core': 'Auth.js',
    '@supabase/supabase-js': 'Supabase',
    '@supabase/auth-helpers-nextjs': 'Supabase Auth (Next.js)',
    '@supabase/ssr': 'Supabase SSR',
    'jsonwebtoken': 'JWT',
    'jose': 'JOSE',
    '@okta/okta-auth-js': 'Okta',
    '@okta/okta-react': 'Okta React',
    'oidc-client-ts': 'OIDC Client',
    '@casl/ability': 'CASL',
    'casbin': 'Casbin',
    'keycloak-js': 'Keycloak',
    '@keycloak/keycloak-admin-client': 'Keycloak Admin',
    'lucia': 'Lucia Auth',
    '@lucia-auth/adapter-prisma': 'Lucia (Prisma)',
    'arctic': 'Arctic (OAuth)',
    'better-auth': 'Better Auth',
    'grant': 'Grant (OAuth)',
    '@workos-inc/node': 'WorkOS',
    'stytch': 'Stytch',
    'frontegg': 'Frontegg',
    '@descope/node-sdk': 'Descope',
    'kinde-auth-nextjs': 'Kinde',
  },
  email: {
    '@sendgrid/mail': 'SendGrid',
    '@sendgrid/client': 'SendGrid Client',
    'nodemailer': 'Nodemailer',
    'twilio': 'Twilio',
    '@aws-sdk/client-ses': 'AWS SES',
    '@aws-sdk/client-sesv2': 'AWS SES v2',
    'postmark': 'Postmark',
    'mailgun.js': 'Mailgun',
    'resend': 'Resend',
    '@react-email/components': 'React Email',
    'react-email': 'React Email',
    'mjml': 'MJML',
    'email-templates': 'Email Templates',
    '@mailchimp/mailchimp_marketing': 'Mailchimp',
    '@mailchimp/mailchimp_transactional': 'Mailchimp Transactional',
    'plunk-node': 'Plunk',
    'brevo': 'Brevo (Sendinblue)',
    'sib-api-v3-sdk': 'Brevo v3',
    '@customer.io/node': 'Customer.io',
    'sparkpost': 'SparkPost',
    'mandrill-api': 'Mandrill',
    'mail-listener5': 'Mail Listener',
    '@azure/communication-email': 'Azure Email',
  },
  cloud: {
    // AWS
    'aws-sdk': 'AWS SDK v2',
    '@aws-sdk/client-s3': 'AWS S3',
    '@aws-sdk/client-lambda': 'AWS Lambda',
    '@aws-sdk/client-dynamodb': 'AWS DynamoDB',
    '@aws-sdk/client-ec2': 'AWS EC2',
    '@aws-sdk/client-ecs': 'AWS ECS',
    '@aws-sdk/client-ecr': 'AWS ECR',
    '@aws-sdk/client-cloudformation': 'AWS CloudFormation',
    '@aws-sdk/client-cloudwatch': 'AWS CloudWatch',
    '@aws-sdk/client-cloudfront': 'AWS CloudFront',
    '@aws-sdk/client-iam': 'AWS IAM',
    '@aws-sdk/client-secrets-manager': 'AWS Secrets Manager',
    '@aws-sdk/client-ssm': 'AWS SSM',
    '@aws-sdk/client-sts': 'AWS STS',
    '@aws-sdk/client-cognito-identity-provider': 'AWS Cognito',
    '@aws-sdk/client-route-53': 'AWS Route 53',
    '@aws-sdk/client-step-functions': 'AWS Step Functions',
    '@aws-sdk/client-eventbridge': 'AWS EventBridge',
    '@aws-sdk/client-kinesis': 'AWS Kinesis',
    '@aws-sdk/client-kms': 'AWS KMS',
    '@aws-sdk/client-athena': 'AWS Athena',
    '@aws-sdk/client-bedrock-runtime': 'AWS Bedrock',
    '@aws-sdk/lib-dynamodb': 'AWS DynamoDB Doc',
    // Azure
    '@azure/storage-blob': 'Azure Blob',
    '@azure/identity': 'Azure Identity',
    '@azure/cosmos': 'Azure Cosmos DB',
    '@azure/keyvault-secrets': 'Azure Key Vault',
    '@azure/service-bus': 'Azure Service Bus',
    '@azure/event-hubs': 'Azure Event Hubs',
    '@azure/functions': 'Azure Functions',
    '@azure/ai-form-recognizer': 'Azure Form Recognizer',
    '@azure/openai': 'Azure OpenAI',
    '@azure/app-configuration': 'Azure App Config',
    '@azure/monitor-opentelemetry': 'Azure Monitor OTel',
    '@azure/container-registry': 'Azure Container Registry',
    // GCP
    '@google-cloud/storage': 'GCP Storage',
    '@google-cloud/functions-framework': 'GCP Functions',
    '@google-cloud/bigquery': 'GCP BigQuery',
    '@google-cloud/firestore': 'GCP Firestore',
    '@google-cloud/pubsub': 'GCP Pub/Sub',
    '@google-cloud/secret-manager': 'GCP Secret Manager',
    '@google-cloud/tasks': 'GCP Cloud Tasks',
    '@google-cloud/run': 'GCP Cloud Run',
    '@google-cloud/logging': 'GCP Logging',
    '@google-cloud/translate': 'GCP Translate',
    '@google-cloud/vertexai': 'GCP Vertex AI',
    // Multi-cloud / PaaS
    '@vercel/sdk': 'Vercel',
    '@vercel/kv': 'Vercel KV',
    '@vercel/blob': 'Vercel Blob',
    '@vercel/postgres': 'Vercel Postgres',
    '@vercel/edge-config': 'Vercel Edge Config',
    '@netlify/functions': 'Netlify Functions',
    '@cloudflare/workers-types': 'Cloudflare Workers',
    'wrangler': 'Wrangler',
    '@railway/cli': 'Railway',
    '@fly/apps': 'Fly.io',
    'heroku-client': 'Heroku',
    '@pulumi/pulumi': 'Pulumi',
    '@pulumi/aws': 'Pulumi AWS',
    'cdktf': 'CDK for Terraform',
    'aws-cdk-lib': 'AWS CDK',
    'sst': 'SST',
  },
  databases: {
    'pg': 'PostgreSQL',
    'postgres': 'Postgres.js',
    'mysql2': 'MySQL',
    'mysql': 'MySQL (legacy)',
    'mongodb': 'MongoDB',
    'ioredis': 'Redis (ioredis)',
    'redis': 'Redis',
    'better-sqlite3': 'SQLite',
    'sql.js': 'sql.js',
    '@prisma/client': 'Prisma',
    'drizzle-orm': 'Drizzle',
    'typeorm': 'TypeORM',
    'sequelize': 'Sequelize',
    'knex': 'Knex',
    'kysely': 'Kysely',
    'objection': 'Objection.js',
    'slonik': 'Slonik',
    'massive': 'Massive.js',
    'mongoose': 'Mongoose',
    '@mikro-orm/core': 'MikroORM',
    'mssql': 'SQL Server',
    'oracledb': 'Oracle',
    'cassandra-driver': 'Cassandra',
    'neo4j-driver': 'Neo4j',
    'arangojs': 'ArangoDB',
    '@clickhouse/client': 'ClickHouse',
    'influx': 'InfluxDB',
    '@libsql/client': 'libSQL (Turso)',
    '@neondatabase/serverless': 'Neon',
    '@planetscale/database': 'PlanetScale',
    '@electric-sql/pglite': 'PGlite',
    '@upstash/redis': 'Upstash Redis',
    '@upstash/kafka': 'Upstash Kafka',
    '@upstash/qstash': 'Upstash QStash',
    '@upstash/vector': 'Upstash Vector',
    'dynamoose': 'Dynamoose',
    'fauna': 'Fauna',
    'faunadb': 'FaunaDB',
    '@supabase/postgrest-js': 'Supabase PostgREST',
    'couchbase': 'Couchbase',
    'couchdb-nano': 'CouchDB',
    'duckdb': 'DuckDB',
    '@tidbcloud/serverless': 'TiDB Serverless',
  },
  messaging: {
    '@aws-sdk/client-sqs': 'AWS SQS',
    '@aws-sdk/client-sns': 'AWS SNS',
    'amqplib': 'RabbitMQ',
    'kafkajs': 'Kafka',
    'bullmq': 'BullMQ',
    'bull': 'Bull',
    '@google-cloud/pubsub': 'GCP Pub/Sub',
    'bee-queue': 'Bee Queue',
    'pg-boss': 'pg-boss',
    'agenda': 'Agenda',
    '@azure/service-bus': 'Azure Service Bus',
    '@azure/event-hubs': 'Azure Event Hubs',
    'nats': 'NATS',
    'mqtt': 'MQTT',
    'zeromq': 'ZeroMQ',
    '@temporalio/client': 'Temporal Client',
    '@temporalio/worker': 'Temporal Worker',
    'inngest': 'Inngest',
    '@trigger.dev/sdk': 'Trigger.dev',
    '@defer/client': 'Defer',
    'graphile-worker': 'Graphile Worker',
    '@quirrel/quirrel': 'Quirrel',
    'rhea': 'AMQP 1.0 (rhea)',
    'stompit': 'STOMP',
    '@eventstore/db-client': 'EventStoreDB',
    '@aws-sdk/client-eventbridge': 'AWS EventBridge',
    '@aws-sdk/client-kinesis': 'AWS Kinesis',
    '@aws-sdk/client-firehose': 'AWS Firehose',
  },
  observability: {
    '@sentry/node': 'Sentry (Node)',
    '@sentry/browser': 'Sentry (Browser)',
    '@sentry/react': 'Sentry (React)',
    '@sentry/nextjs': 'Sentry (Next.js)',
    '@sentry/vue': 'Sentry (Vue)',
    '@sentry/angular': 'Sentry (Angular)',
    '@opentelemetry/api': 'OpenTelemetry API',
    '@opentelemetry/sdk-node': 'OpenTelemetry SDK',
    '@opentelemetry/auto-instrumentations-node': 'OpenTelemetry Auto',
    'applicationinsights': 'App Insights',
    'dd-trace': 'Datadog',
    '@datadog/browser-rum': 'Datadog RUM',
    'newrelic': 'New Relic',
    '@newrelic/browser-agent': 'New Relic Browser',
    'pino': 'Pino',
    'winston': 'Winston',
    'bunyan': 'Bunyan',
    'consola': 'Consola',
    'loglevel': 'loglevel',
    'roarr': 'Roarr',
    'prom-client': 'Prometheus',
    'elastic-apm-node': 'Elastic APM',
    '@logtail/node': 'Logtail',
    '@axiomhq/pino': 'Axiom (Pino)',
    '@axiomhq/winston': 'Axiom (Winston)',
    '@baselime/node-opentelemetry': 'Baselime',
    'highlight.run': 'Highlight',
    '@google-cloud/trace-agent': 'GCP Trace',
    '@google-cloud/logging': 'GCP Logging',
    '@azure/monitor-opentelemetry': 'Azure Monitor',
    'lightstep-tracer': 'Lightstep',
    'honeycomb-beeline': 'Honeycomb',
    '@honeycombio/opentelemetry-node': 'Honeycomb OTel',
    'posthog-node': 'PostHog',
    '@amplitude/node': 'Amplitude',
    'mixpanel': 'Mixpanel',
    '@segment/analytics-node': 'Segment',
    'analytics-node': 'Segment (legacy)',
    '@rudderstack/rudder-sdk-node': 'RudderStack',
    'heap-api': 'Heap',
    '@fullstory/browser': 'FullStory',
    '@logrocket/react': 'LogRocket',
    'logrocket': 'LogRocket',
    'hotjar-js': 'Hotjar',
    '@clarity-ai/clarity': 'Microsoft Clarity',
  },
  crm: {
    'xero-node': 'Xero',
    'hubspot-api-client': 'HubSpot',
    '@hubspot/api-client': 'HubSpot',
    '@slack/web-api': 'Slack Web API',
    '@slack/bolt': 'Slack Bolt',
    '@slack/events-api': 'Slack Events',
    'discord.js': 'Discord',
    'intercom-client': 'Intercom',
    'salesforce-sdk': 'Salesforce',
    'jsforce': 'JSforce (Salesforce)',
    '@zendesk/sell-client': 'Zendesk',
    'node-zendesk': 'Zendesk',
    'freshdesk-api': 'Freshdesk',
    'pipedrive': 'Pipedrive',
    'airtable': 'Airtable',
    '@notionhq/client': 'Notion',
    '@linear/sdk': 'Linear',
    'jira-client': 'Jira',
    'jira.js': 'Jira.js',
    '@octokit/rest': 'GitHub (Octokit)',
    '@octokit/core': 'GitHub (Octokit Core)',
    'gitlab': 'GitLab',
    '@gitbeaker/rest': 'GitLab (Gitbeaker)',
    'clickup.js': 'ClickUp',
    'asana': 'Asana',
    'monday-sdk-js': 'Monday.com',
    'telegram-bot-api': 'Telegram',
    'telegraf': 'Telegraf (Telegram)',
    '@microsoft/microsoft-graph-client': 'Microsoft Graph',
    'googleapis': 'Google APIs',
    '@google-cloud/aiplatform': 'Google AI Platform',
  },
  storage: {
    '@aws-sdk/client-s3': 'AWS S3',
    '@aws-sdk/s3-request-presigner': 'AWS S3 Presigner',
    '@azure/storage-blob': 'Azure Blob',
    '@azure/storage-queue': 'Azure Storage Queue',
    '@azure/storage-file-share': 'Azure File Share',
    '@google-cloud/storage': 'GCP Storage',
    'minio': 'MinIO',
    '@supabase/storage-js': 'Supabase Storage',
    'cloudinary': 'Cloudinary',
    'uploadthing': 'UploadThing',
    '@uploadthing/react': 'UploadThing React',
    'multer': 'Multer',
    'multer-s3': 'Multer S3',
    'formidable': 'Formidable',
    'busboy': 'Busboy',
    '@vercel/blob': 'Vercel Blob',
    'imagekit': 'ImageKit',
    'sharp': 'Sharp (image)',
    'jimp': 'Jimp (image)',
    'fluent-ffmpeg': 'FFmpeg',
    '@google-cloud/vision': 'GCP Vision',
    '@aws-sdk/client-rekognition': 'AWS Rekognition',
    'file-type': 'file-type',
    '@backblaze-b2/sdk': 'Backblaze B2',
    'wasabi-sdk': 'Wasabi',
  },
  search: {
    '@elastic/elasticsearch': 'Elasticsearch',
    'algoliasearch': 'Algolia',
    '@algolia/client-search': 'Algolia Client',
    'react-instantsearch': 'Algolia InstantSearch',
    'meilisearch': 'Meilisearch',
    'typesense': 'Typesense',
    '@opensearch-project/opensearch': 'OpenSearch',
    'solr-client': 'Solr',
    'lunr': 'Lunr',
    'flexsearch': 'FlexSearch',
    'fuse.js': 'Fuse.js',
    'minisearch': 'MiniSearch',
    'orama': 'Orama',
    '@orama/orama': 'Orama',
    '@trieve/trieve-ts-sdk': 'Trieve',
    'pagefind': 'Pagefind',
    // Vector / AI search
    '@pinecone-database/pinecone': 'Pinecone',
    'chromadb': 'Chroma',
    '@qdrant/js-client-rest': 'Qdrant',
    'weaviate-ts-client': 'Weaviate',
    '@zilliz/milvus2-sdk-node': 'Milvus',
    'openai': 'OpenAI',
    '@anthropic-ai/sdk': 'Anthropic',
    '@langchain/core': 'LangChain',
    'langchain': 'LangChain',
    '@google/generative-ai': 'Google Gemini',
    'cohere-ai': 'Cohere',
    'replicate': 'Replicate',
    '@huggingface/inference': 'Hugging Face',
    'ai': 'Vercel AI SDK',
    '@ai-sdk/openai': 'AI SDK (OpenAI)',
  },
};

export function scanServiceDependencies(projects: ProjectScan[]): ServiceDependenciesResult {
  // Build package → version map from all projects
  const packageVersions = new Map<string, string | null>();

  for (const project of projects) {
    for (const dep of project.dependencies) {
      if (!packageVersions.has(dep.package)) {
        packageVersions.set(dep.package, dep.resolvedVersion);
      }
    }
  }

  const result: ServiceDependenciesResult = {
    payment: [],
    auth: [],
    email: [],
    cloud: [],
    databases: [],
    messaging: [],
    observability: [],
    crm: [],
    storage: [],
    search: [],
  };

  for (const [category, packages] of Object.entries(SERVICE_CATEGORIES)) {
    const items: ServiceDependencyItem[] = [];
    for (const [pkg, displayName] of Object.entries(packages)) {
      if (packageVersions.has(pkg)) {
        items.push({
          name: displayName,
          package: pkg,
          version: packageVersions.get(pkg) ?? null,
        });
      }
    }
    items.sort((a, b) => a.name.localeCompare(b.name));
    (result as unknown as Record<string, ServiceDependencyItem[]>)[category] = items;
  }

  return result;
}
